/**
 * resolveBundleHeaderCellValue
 *
 * Single source of truth for what an ArTable bundle-header cell means, shared
 * by the three surfaces that render one: ArBundleHeaderRow (screen and PDF),
 * createArTableExportSnapshot and useArTableExport (XLSX). Pure function — no
 * store access, no React.
 *
 * Invariant: 'ownCell' and 'driver' resolutions mean cells[typeId].value and
 * NEVER .calcInput — real header docs carry stale calcInput zeros written by
 * the pre-SYD-2276 formula pass, and those must not print on any surface.
 *
 * `rowData` is typed as a Valtio snapshot: the screen hands over a deeply
 * readonly `useSnapshot` row, and a plain persisted `ArRowData` — what both
 * XLSX paths hold — is assignable to it, so no caller casts.
 */

import type { ArBundleType, ArColumnDefinition, ArRowData, TypeId } from '#types';
import { UIElementType } from '#types';
import type { Snapshot } from 'valtio';

export type BundleHeaderCellResolution =
  | { kind: 'bundleValue' }
  | { kind: 'ownCell' }
  | { kind: 'driver' }
  | { kind: 'aggregation'; value: number | string | null }
  | { kind: 'blank' };

function resolveBundleHeaderCellValue(params: {
  rowData: Snapshot<ArRowData> | undefined;
  column: ArColumnDefinition;
  bundleType: ArBundleType;
  kitCalcDriverTypeIds: ReadonlySet<TypeId>;
  isFirstColumn: boolean;
}): BundleHeaderCellResolution {
  const { rowData, column, bundleType, kitCalcDriverTypeIds, isFirstColumn } = params;

  if (isFirstColumn && bundleType === 'group') return { kind: 'bundleValue' };
  if (isFirstColumn && bundleType === 'kit') return { kind: 'ownCell' };
  if (bundleType === 'kit' && kitCalcDriverTypeIds.has(column.typeId)) return { kind: 'driver' };
  if (column.uiElementType === UIElementType.Field && !!column.aggregation) {
    return {
      kind: 'aggregation',
      value: rowData?.bundleAggregations?.[column.typeId]?.value ?? null,
    };
  }
  return { kind: 'blank' };
}

export { resolveBundleHeaderCellValue };
